import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import { getAvailability } from '../api';

// common/v7 booking_availability — a shared, dependency-free month-grid date picker that DISABLES
// unavailable dates (rather than letting the user pick and fail later) and shows a tooltip on
// every disabled day: "Unavailable — {reason}". Ported from gym's AvailabilityCalendar (v8
// stock_and_availability follow-up - car_rental/jewelry/hospitality/real_estate previously had no
// calendar at all, just plain date inputs that only surfaced a conflict from the 409 at submit).
//
// Props:
//   resourceId            the RentalItem id to check availability for
//   mode                  'single' (default) | 'range'
//   value                 'YYYY-MM-DD'  (single)  |  { start, end }  (range)
//   onChange(next)        same shape as `value`
//   roomTypeId, memberEmail  extra query params passed straight to GET /availability
//   monthsShown           1 (default) or 2
//   extraBlocked          [{ start, end, reason, conflictType }] merged with the server feed
//   minDate, maxDate      'YYYY-MM-DD' overrides (else server-provided / today..+1y)

const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// Colour a disabled day by how hard the conflict is.
const SEVERITY = {
  ALREADY_BOOKED: 'hard', ALREADY_RESERVED: 'hard', MAINTENANCE: 'hard', BLOCKED_DATE: 'hard',
  MEMBERSHIP_ACTIVE: 'hard', LISTING_UNAVAILABLE: 'hard', CAPACITY_EXCEEDED: 'hard',
  BUSINESS_CLOSED: 'soft', ON_HOLIDAY: 'soft', ON_LEAVE: 'soft', ALREADY_ASSIGNED: 'soft',
  TIME_CONFLICT: 'soft', OUTSIDE_BUSINESS_HOURS: 'soft'
};

function ymd(d) {
  const x = d instanceof Date ? d : new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
}
function parseYmd(s) {
  if (!s) return null;
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
}
function addMonths(d, n) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function sameYmd(a, b) {
  return a && b && ymd(a) === ymd(b);
}

export default function AvailabilityCalendar({
  resourceId, mode = 'single', value, onChange,
  roomTypeId, memberEmail, monthsShown = 1, extraBlocked = [],
  minDate, maxDate, onFeedError
}) {
  const today = useMemo(() => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; }, []);
  const [cursor, setCursor] = useState(() => {
    const v = mode === 'range' ? value?.start : value;
    return parseYmd(v) ? new Date(parseYmd(v).getFullYear(), parseYmd(v).getMonth(), 1) : new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [feed, setFeed] = useState({ blocked: [], minDate: null, maxDate: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const windowFrom = ymd(new Date(cursor.getFullYear(), cursor.getMonth(), 1));
  const windowTo = ymd(addMonths(cursor, monthsShown + 1));

  const load = useCallback(async () => {
    if (!resourceId) return;
    setLoading(true);
    setError('');
    try {
      const data = await getAvailability({ resourceId, from: windowFrom, to: windowTo, roomTypeId, memberEmail });
      setFeed(data);
      setError('');
      if (onFeedError) onFeedError(false);
    } catch (e) {
      setError('Could not check availability. Dates shown may be inaccurate — retry before booking.');
      if (onFeedError) onFeedError(true);
    } finally {
      setLoading(false);
    }
  }, [resourceId, windowFrom, windowTo, roomTypeId, memberEmail]);

  useEffect(() => { load(); }, [load]);

  const blocked = useMemo(() => [...(feed.blocked || []), ...(extraBlocked || [])], [feed, extraBlocked]);

  const lo = parseYmd(minDate) || parseYmd(feed.minDate) || today;
  const hi = parseYmd(maxDate) || parseYmd(feed.maxDate) || new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());

  // For a given day, the first blocking span that covers it (spans are [start,end)).
  function conflictFor(day) {
    const t = day.getTime();
    const dayEnd = t + 86400000;
    for (const b of blocked) {
      const bs = new Date(b.start).getTime();
      const be = new Date(b.end).getTime();
      if (bs < dayEnd && t < be) return b;
    }
    return null;
  }

  function dayState(day) {
    if (day < lo || day > hi) return { disabled: true, reason: day < today ? 'This date is in the past.' : 'Too far ahead to book.' };
    const c = conflictFor(day);
    if (c) return { disabled: true, reason: c.reason || 'Unavailable.', conflictType: c.conflictType };
    return { disabled: false };
  }

  const selStart = mode === 'range' ? parseYmd(value?.start) : parseYmd(value);
  const selEnd = mode === 'range' ? parseYmd(value?.end) : null;

  function inRange(day) {
    if (mode !== 'range' || !selStart || !selEnd) return false;
    return day > selStart && day < selEnd;
  }

  // Range mode: reject a selection that would span a blocked day.
  function rangeSpansBlocked(a, b) {
    for (let d = new Date(a.getTime() + 86400000); d < b; d = new Date(d.getTime() + 86400000)) {
      if (conflictFor(d)) return true;
    }
    return false;
  }

  function pick(day) {
    const st = dayState(day);
    if (st.disabled) return;
    if (mode === 'single') { onChange(ymd(day)); return; }
    // range
    if (!selStart || (selStart && selEnd) || day <= selStart) {
      onChange({ start: ymd(day), end: '' });
      return;
    }
    if (rangeSpansBlocked(selStart, day)) {
      setError('That range crosses an unavailable date — pick an end date before it.');
      return;
    }
    setError('');
    onChange({ start: ymd(selStart), end: ymd(day) });
  }

  function renderMonth(base) {
    const first = new Date(base.getFullYear(), base.getMonth(), 1);
    const startPad = first.getDay();
    const daysInMonth = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startPad; i += 1) cells.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) cells.push(new Date(base.getFullYear(), base.getMonth(), d));

    return (
      <div key={ymd(base)} className="flex-fill" style={{ minWidth: 240 }}>
        <div className="text-center fw-semibold mb-1">{MONTHS[base.getMonth()]} {base.getFullYear()}</div>
        <div className="d-grid" style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
          {DOW.map((d, i) => <div key={i} className="text-center text-muted small">{d}</div>)}
          {cells.map((day, i) => {
            if (!day) return <div key={i} />;
            const st = dayState(day);
            const isSel = sameYmd(day, selStart) || sameYmd(day, selEnd);
            const rng = inRange(day);
            const sev = st.conflictType ? SEVERITY[st.conflictType] || 'soft' : null;
            const cell = (
              <button
                type="button"
                disabled={st.disabled}
                onClick={() => pick(day)}
                aria-label={`${ymd(day)}${st.disabled ? ` — ${st.reason}` : ''}`}
                className={[
                  'btn btn-sm w-100 position-relative p-1',
                  isSel ? 'btn-primary' : rng ? 'btn-primary bg-opacity-25 border-0' : st.disabled ? 'btn-light text-muted' : 'btn-outline-secondary'
                ].join(' ')}
                style={st.disabled ? { textDecoration: sev === 'hard' ? 'line-through' : 'none', opacity: sev === 'hard' ? 0.55 : 0.8, cursor: 'not-allowed' } : undefined}
              >
                {day.getDate()}
                {st.disabled && sev && (
                  <span
                    aria-hidden
                    className="position-absolute"
                    style={{ bottom: 2, left: '50%', transform: 'translateX(-50%)', width: 5, height: 5, borderRadius: '50%', background: sev === 'hard' ? '#dc3545' : '#fd7e14' }}
                  />
                )}
              </button>
            );
            if (!st.disabled) return <div key={i}>{cell}</div>;
            return (
              <OverlayTrigger key={i} placement="top" overlay={<Tooltip id={`av-${ymd(day)}`}><strong>Unavailable</strong><br />{st.reason}</Tooltip>}>
                <div>{cell}</div>
              </OverlayTrigger>
            );
          })}
        </div>
      </div>
    );
  }

  const months = Array.from({ length: monthsShown }, (_, i) => addMonths(cursor, i));
  const canPrev = cursor > new Date(today.getFullYear(), today.getMonth(), 1);

  return (
    <div className="border rounded p-2">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <Button size="sm" variant="outline-secondary" disabled={!canPrev} onClick={() => setCursor(addMonths(cursor, -1))}>‹</Button>
        {loading && <Spinner animation="border" size="sm" role="status" />}
        <Button size="sm" variant="outline-secondary" onClick={() => setCursor(addMonths(cursor, 1))}>›</Button>
      </div>
      <div className="d-flex flex-wrap gap-3">
        {months.map(renderMonth)}
      </div>
      {error && (
        <div className="small text-danger mt-2 d-flex align-items-center gap-2">
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-outline-secondary py-0" onClick={load}>Retry</button>
        </div>
      )}
      <div className="small text-muted mt-2 d-flex flex-wrap gap-3">
        <span><span className="d-inline-block rounded-circle me-1" style={{ width: 7, height: 7, background: '#dc3545' }} />Booked / blocked</span>
        <span><span className="d-inline-block rounded-circle me-1" style={{ width: 7, height: 7, background: '#fd7e14' }} />Closed / on leave</span>
      </div>
    </div>
  );
}
